/**
 * OPT-06 后台账号运维 API（23 号 §5.3 / 25 号 §4.2）。
 *
 * 对应后端：admin-account.controller.ts + admin-account.service.ts
 * - 列表/新建/修改/重置密码/软删除：仅 SUPER_ADMIN；
 * - 本人改密 `/auth/password`：登录即可（携带 HttpOnly Cookie 撤销本人其他会话）。
 *
 * 不变量：任何响应都不含 passwordHash 或密码明文，前端也不得把密码写入日志/页面。
 */
import type { AdminAccountStatus, AdminRoleCode } from '@liyu/shared-types';
import type { ApiMeta } from '@liyu/shared-types';
import { apiRequest } from './client';
import type { Paginated } from './types';

/** 与后端 PasswordService 标准一致（admin-account.service.ts） */
export const ADMIN_PASSWORD_MIN_LENGTH = 8;
export const ADMIN_PASSWORD_MAX_LENGTH = 128;

export interface AdminAccountRow {
  id: string;
  email: string;
  displayName: string;
  status: AdminAccountStatus;
  roles: AdminRoleCode[];
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminAccountListQuery {
  status?: AdminAccountStatus;
  keyword?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminAccountCreateInput {
  email: string;
  displayName: string;
  roles: AdminRoleCode[];
  password: string;
}

export interface AdminAccountUpdateInput {
  displayName?: string;
  roles?: AdminRoleCode[];
  status?: AdminAccountStatus;
  /** 变更角色/状态必填（服务端强校验） */
  reason?: string;
}

export interface AdminAccountPasswordResetResult {
  id: string;
  email: string;
  passwordChangedAt: string;
}

export interface AdminAccountDeleteResult {
  id: string;
  email: string;
  deletedAt: string;
}

export interface ChangeOwnPasswordResult {
  id: string;
  passwordChangedAt: string;
  revokedOtherSessions: number;
}

function toPaginated<T>(data: T[], meta?: ApiMeta, fallbackSize = 20): Paginated<T> {
  return {
    items: data,
    total: meta?.total ?? data.length,
    page: meta?.page ?? 1,
    pageSize: meta?.pageSize ?? fallbackSize,
  };
}

export async function listAdminAccounts(
  query: AdminAccountListQuery = {},
): Promise<Paginated<AdminAccountRow>> {
  const result = await apiRequest<AdminAccountRow[]>('/admin-accounts', {
    query: {
      status: query.status,
      keyword: query.keyword,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function createAdminAccount(body: AdminAccountCreateInput): Promise<AdminAccountRow> {
  return (await apiRequest<AdminAccountRow>('/admin-accounts', { method: 'POST', body })).data;
}

export async function updateAdminAccount(
  id: string,
  body: AdminAccountUpdateInput,
): Promise<AdminAccountRow> {
  return (await apiRequest<AdminAccountRow>(`/admin-accounts/${id}`, { method: 'PATCH', body }))
    .data;
}

export async function resetAdminAccountPassword(
  id: string,
  body: { password: string; reason?: string },
): Promise<AdminAccountPasswordResetResult> {
  return (
    await apiRequest<AdminAccountPasswordResetResult>(`/admin-accounts/${id}/reset-password`, {
      method: 'POST',
      body,
    })
  ).data;
}

export async function deleteAdminAccount(
  id: string,
  reason: string,
): Promise<AdminAccountDeleteResult> {
  return (
    await apiRequest<AdminAccountDeleteResult>(`/admin-accounts/${id}`, {
      method: 'DELETE',
      body: { reason },
    })
  ).data;
}

export async function changeOwnPassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<ChangeOwnPasswordResult> {
  return (await apiRequest<ChangeOwnPasswordResult>('/auth/password', { method: 'POST', body }))
    .data;
}
